import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Pill, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { canUseWebAuthn, loginWithPasskeyDiscoverable, supportsConditionalUI } from "@/lib/webauthn";
import { AboutMedCarerDialog } from "@/components/AboutMedCarerDialog";
import ninePointsLogo from "@/assets/9points-logo.webp";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("invitation");
  const conditionalAbortRef = useRef<AbortController | null>(null);

  const showBiometricHint = canUseWebAuthn();

  // Post-auth routing shared by all sign-in paths (password, OTP, passkey).
  // Mirrors the gating done after password login: approval → must_change_password → terms.
  const routeAfterLogin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/dashboard"); return; }
    let pendingInvite: string | null = null;
    try { pendingInvite = sessionStorage.getItem("pending_child_invite"); } catch { /* storage unavailable */ }
    const { data: profile } = await supabase
      .from("profiles")
      .select("must_change_password, terms_accepted_at, onboarding_completed")
      .eq("user_id", user.id)
      .maybeSingle();
    if (profile?.must_change_password) { navigate("/change-password"); return; }
    if (!profile?.terms_accepted_at) { navigate("/terms"); return; }
    // Carer invitation token in URL — explicitly claim it now (consent gate)
    if (invitationToken) {
      try {
        await supabase.functions.invoke("claim-invitation", { body: { invitationToken } });
      } catch (err) {
        console.error("Failed to claim carer invitation:", err);
      }
    }
    if (pendingInvite) { navigate(`/accept-co-parent?token=${pendingInvite}`); return; }
    // First-login carer onboarding: show the welcome screen once.
    if (!profile?.onboarding_completed) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      const isCarer = roles?.some((r) => r.role === "carer");
      const isPatient = roles?.some((r) => r.role === "patient");
      if (isCarer && !isPatient) { navigate("/carer-onboarding"); return; }
    }
    navigate("/dashboard");
  };

  // Conditional UI: silently arm Face ID via the email field's autofill.
  useEffect(() => {
    if (!canUseWebAuthn()) {
      console.log("[WebAuthn] Conditional UI skipped: WebAuthn not available in this context.");
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    conditionalAbortRef.current = controller;

    (async () => {
      const supported = await supportsConditionalUI();
      console.log("[WebAuthn] Conditional mediation supported:", supported);
      if (!supported || cancelled) return;
      try {
        console.log("[WebAuthn] Arming conditional UI — Face ID will be offered when the email field is focused.");
        await loginWithPasskeyDiscoverable({ conditional: true, signal: controller.signal });
        if (cancelled) return;
        console.log("[WebAuthn] Conditional UI authentication succeeded.");
        await routeAfterLogin();
      } catch (err) {
        console.log("[WebAuthn] Conditional UI ended:", (err as Error)?.message || err);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      toast({ variant: "destructive", title: "Login failed", description: error.message });
      return;
    }

    if (data.user) {
      await routeAfterLogin();
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 bg-background">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Pill className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">MedCarer</CardTitle>
          <div className="flex items-center justify-center gap-1 mt-1">
            <span className="text-xs text-muted-foreground">By 9Points</span>
            <img src={ninePointsLogo} alt="9Points logo" className="h-3.5 w-3.5 object-contain" />
          </div>
          <CardDescription className="mt-2">Sign in to manage medications</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                autoComplete="username webauthn"
              />
              {showBiometricHint && (
                <p className="text-xs text-muted-foreground md:hidden">
                  Tap the email field to sign in with Face ID / Touch ID
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
            {loading && (
              <p className="text-xs text-center text-muted-foreground">
                Please wait — verifying your account…
              </p>
            )}
          </form>

          <div className="mt-4 text-center text-sm space-y-3">
            <Link to="/forgot-password" className="text-primary hover:underline">Forgot password?</Link>
            <div className="rounded-lg bg-accent/50 p-3">
              <p className="text-base font-semibold text-foreground">
                New to MedCarer, sign up for a free account?{" "}
                <Link to="/signup" className="text-primary underline hover:no-underline">Sign up</Link>
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
            </p>
            <div className="pt-1">
              <AboutMedCarerDialog />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
